// Objekt-Umschalter in der Titelzeile (Screen eigentuemer/05). Nur mit mehr als einem Objekt aufklappbar.
// Schließt bei Esc und Klick daneben. #584: „über …“ je Objekt und „Alle Objekte“ zurück zur Vorauswahl.
import { useEffect, useRef, useState } from 'react';
import { Buildings, BuildingApartment, CaretDown, Check, SquaresFour } from '@phosphor-icons/react';
import { IconChip } from '../../components/portal/bausteine';
import { artIstWeg, umschalterZeile, type ObjektSicht } from '../../lib/portal/eigentuemer';

export default function ObjektUmschalter({ objekte, aktiv, onWahl, ueber, onAlle }: {
  objekte: ObjektSicht[];
  aktiv: ObjektSicht;
  onWahl: (id: string) => void;
  /** Zusatzzeile je Objekt, z. B. „über MR Wohnraum GmbH“ */
  ueber?: (o: ObjektSicht) => string | null;
  /** zurück zur Vorauswahl aller Objekte */
  onAlle?: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    const klick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false); };
    document.addEventListener('keydown', taste);
    document.addEventListener('mousedown', klick);
    return () => { document.removeEventListener('keydown', taste); document.removeEventListener('mousedown', klick); };
  }, [offen]);

  const titel = <h1 className="ep-titel">{aktiv.name}</h1>;
  if (objekte.length < 2) return titel;

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0 }}>
      <button type="button" className="ep-umschalter" onClick={() => setOffen((o) => !o)} aria-haspopup="listbox" aria-expanded={offen}
        data-tip="Objekt wechseln" aria-label={`Objekt wechseln, gewählt: ${aktiv.name}`}>
        {titel}
        <span className="ep-caret" style={{ transform: offen ? 'rotate(180deg)' : undefined }}><CaretDown size={14} weight="bold" /></span>
      </button>
      {offen && (
        <div className="p-menue p-rein ep-objektmenue" role="listbox" aria-label="Ihre Objekte">
          <span className="mp-label" style={{ padding: '6px 10px 4px', fontSize: 11 }}>Ihre Objekte</span>
          {objekte.map((o) => {
            const an = o.objekt.id === aktiv.objekt.id;
            return (
              <button key={o.objekt.id} type="button" role="option" aria-selected={an} className="ep-objektwahl"
                style={{ background: an ? 'var(--p-gold-fl)' : undefined }}
                onClick={() => { onWahl(o.objekt.id); setOffen(false); }}>
                <IconChip farbe={artIstWeg(o.art) ? 'gemein' : 'wohnung'} icon={artIstWeg(o.art) ? BuildingApartment : Buildings} groesse={36} />
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                  <span className="p-kuerzen" style={{ fontSize: 14, fontWeight: 800 }}>{o.name}</span>
                  <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>{umschalterZeile(o)}</span>
                  {ueber?.(o) && <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 700, color: 'var(--p-gold-text)' }}>{ueber(o)}</span>}
                </span>
                {an && <Check size={16} weight="bold" style={{ color: 'var(--p-gold-text)' }} />}
              </button>
            );
          })}
          {onAlle && (
            <button type="button" className="ep-objektwahl ep-objektwahl-alle" onClick={() => { onAlle(); setOffen(false); }}>
              <SquaresFour size={18} weight="bold" style={{ marginLeft: 9, marginRight: 9 }} />
              <span>Alle Objekte im Überblick</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
